// src/components/workAccountability/PersonRulesSection.tsx
//
// Part 4: Work Accountability person rules inside the Team Member slide-over.
// Lists a member's active rules and lets a manager assign / remove (expire) them.
// Everything is driven by the existing person-rule APIs.

import { useState } from 'react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import ConfirmDialog from '../ui/ConfirmDialog';
import {
  useGetPersonRulesQuery,
  useAssignPersonRuleMutation,
  useExpirePersonRuleMutation,
} from '../../api/accountabilityApi';
import type { AccountabilityPolicy, WorkAccountabilityRules } from '../../types/workAccountability';
import { SYSTEM_DEFAULT_RULES, requirementsSummary } from './waFormat';
import { Banner } from './waControls';
import { useCanManageAccountability } from '../../hooks/useWorkAccountability';
import RequirementsEditor from './RequirementsEditor';

interface Props {
  workspaceId: string;
  userId: string;
  /** The viewed member is manageable (not an owner). Combined with the CURRENT
   *  user's owner/admin privilege below — never trust this alone. */
  canManage: boolean;
}

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
}

export default function PersonRulesSection({ userId, canManage }: Props) {
  // Server-authoritative gate: the CURRENT user must be owner/admin (not just the
  // viewed member being non-owner). Fixes the client-side authorization leak.
  const canManageWa = useCanManageAccountability() && canManage;
  const { data: rules, isLoading, isError } = useGetPersonRulesQuery({ userId }, { skip: !userId });
  const [assign, { isLoading: assigning }] = useAssignPersonRuleMutation();
  const [expire] = useExpirePersonRuleMutation();

  const [assignOpen, setAssignOpen] = useState(false);
  const [draftRules, setDraftRules] = useState<WorkAccountabilityRules>(SYSTEM_DEFAULT_RULES);
  const [reason, setReason] = useState('');
  const [effectiveAt, setEffectiveAt] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [banner, setBanner] = useState<{ kind: 'success' | 'error'; message: string } | null>(null);
  const [confirmRemove, setConfirmRemove] = useState<AccountabilityPolicy | null>(null);

  const openAssign = () => {
    setDraftRules(SYSTEM_DEFAULT_RULES);
    setReason('');
    setEffectiveAt('');
    setExpiresAt('');
    setBanner(null);
    setAssignOpen(true);
  };

  const submitAssign = async () => {
    setBanner(null);
    try {
      await assign({
        userId,
        rules: draftRules,
        assignmentReason: reason.trim() || null,
        effectiveAt: effectiveAt || null,
        expiresAt: expiresAt || null,
      }).unwrap();
      setAssignOpen(false);
      setBanner({ kind: 'success', message: 'Rule assigned.' });
    } catch (e) {
      setBanner({ kind: 'error', message: (e as { data?: { error?: string } })?.data?.error ?? 'Could not assign the rule.' });
    }
  };

  const doRemove = async () => {
    const r = confirmRemove;
    if (!r) return;
    try {
      await expire({ id: r.id, expectedVersion: r.version }).unwrap();
      setBanner({ kind: 'success', message: 'Rule removed.' });
    } catch (e) {
      setBanner({ kind: 'error', message: (e as { data?: { error?: string } })?.data?.error ?? 'Could not remove the rule.' });
    } finally {
      setConfirmRemove(null);
    }
  };

  return (
    <div data-testid="wa-person-rules">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-sm font-medium text-gray-700">Work Accountability</h4>
        {canManageWa && (
          <Button size="sm" variant="outline" onClick={openAssign}>
            Assign Rule
          </Button>
        )}
      </div>

      {/* Success/other banners outside the modal; the assign error banner lives
          INSIDE the modal (below) so it is never hidden behind the overlay. */}
      {banner && banner.kind !== 'error' && <div className="mb-2"><Banner kind={banner.kind} message={banner.message} /></div>}
      {banner && banner.kind === 'error' && !assignOpen && <div className="mb-2"><Banner kind="error" message={banner.message} /></div>}

      {isLoading ? (
        <div className="h-16 w-full animate-pulse rounded-lg bg-gray-100" />
      ) : isError ? (
        <p className="text-sm text-gray-500">Could not load rules for this member.</p>
      ) : !rules || rules.length === 0 ? (
        <p className="text-sm text-gray-500">No active rules for this member.</p>
      ) : (
        <ul className="space-y-2">
          {rules.map((r) => (
            <li key={r.id} className="rounded-lg border border-gray-200 p-3" data-testid={`wa-person-rule-${r.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 text-sm">
                  <div className="text-gray-800">{requirementsSummary(r.rules).join(' · ')}</div>
                  <dl className="mt-1 text-xs text-gray-500 space-y-0.5">
                    <div><span className="font-medium">Reason:</span> {r.assignmentReason || '—'}</div>
                    <div><span className="font-medium">Effective:</span> {fmtDate(r.effectiveAt)} <span className="font-medium ml-2">Expires:</span> {fmtDate(r.expiresAt)}</div>
                  </dl>
                </div>
                {canManageWa && (
                  <Button size="sm" variant="ghost" onClick={() => setConfirmRemove(r)}>
                    Remove
                  </Button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      <Modal
        isOpen={assignOpen}
        onClose={() => setAssignOpen(false)}
        title="Assign a rule"
        size="md"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setAssignOpen(false)} disabled={assigning}>Cancel</Button>
            <Button variant="primary" onClick={submitAssign} isLoading={assigning}>Assign</Button>
          </div>
        }
      >
        <div className="space-y-4">
          {banner && banner.kind === 'error' && <Banner kind="error" message={banner.message} />}
          <div>
            <label htmlFor="wa-rule-reason" className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
            <input
              id="wa-rule-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why this rule applies (kept private to managers)"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="wa-rule-eff" className="block text-sm font-medium text-gray-700 mb-1">Effective date</label>
              <input id="wa-rule-eff" type="date" value={effectiveAt} onChange={(e) => setEffectiveAt(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
            </div>
            <div>
              <label htmlFor="wa-rule-exp" className="block text-sm font-medium text-gray-700 mb-1">Expiration</label>
              <input id="wa-rule-exp" type="date" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
            </div>
          </div>
          <div className="border-t border-gray-100 pt-3">
            <div className="text-sm font-semibold text-gray-900 mb-2">Requirements</div>
            <RequirementsEditor rules={draftRules} onChange={setDraftRules} />
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        isOpen={!!confirmRemove}
        title="Remove rule?"
        message="This person rule will stop applying. Prior completion records are unaffected."
        confirmLabel="Remove"
        variant="warning"
        onConfirm={doRemove}
        onCancel={() => setConfirmRemove(null)}
      />
    </div>
  );
}
